/* eslint-disable react/prop-types */
import { useState } from "react";
import useMediaQuery from "@repo/shared-ui/useMediaQuery";
import BFLLink from "@repo/shared-ui/BFLLink";

interface ProductLink {
  linkTitle?: string;
  linkUrl?: string;
  newTab?: string;
}

interface ProductSection {
  sectionTitle?: string;
  titleUrl?: string;
  links?: ProductLink[];
}

interface ProductProps {
  data: ProductSection[]; // Flat array of 13 items from CMS
}

const Products: React.FC<ProductProps> = ({ data = [] }) => {
  const [openMobile, setOpenMobile] = useState<string | null>(null);
  const isMobile = useMediaQuery("(max-width: 767px)");

  const handleMobileToggle = (title: string, isOpen: boolean) => {
    if (window.innerWidth < 768) setOpenMobile(isOpen ? null : title);
  };

  // Group the 13 flat items by their exact CMS index order (structure is fixed by CMS).
  // This keeps the column layout consistent across locales.
  const regularColumns = [
    data.slice(0, 1), // Column 1: Loans (Index 0)
    data.slice(1, 3), // Column 2: Insurance, Finance for Professionals (Indices 1, 2)
    data.slice(3, 5), // Column 3: Investments, Pocket Subscription (Indices 3, 4)
    data.slice(5, 7), // Column 4: Bajaj Mall, Services (Indices 5, 6)
    data.slice(7, 10), // Column 5: Wallets & Cards, Value Added, Payments (Indices 7, 8, 9)
  ];

  // Column 6: Pre-approved Offers, Offer World, Articles (Indices 10, 11, 12)
  const specialSections = data.slice(10, 13);

  return (
    <div className="w-full">
      <div className="grid md:grid-cols-6 md:gap-8">
        {/* Render Regular Columns (1 to 5) */}
        {regularColumns.map((sections, colIdx) => {
          // Create a stable key for each column
          const colKey = sections.map((s) => s?.sectionTitle ?? "").join("-") || String(colIdx);

          return (
            <div key={colKey}>
              {sections.map((section, sidx: number) => {
                if (!section) return null;

                const sectionTitle = section.sectionTitle?.trim() ?? "";
                const isOpen = openMobile === sectionTitle;

                // Add top margin to the second/third items in a column (e.g., Services under Bajaj Mall)
                const sectionClassName = sidx > 0 ? "mt-4" : "";

                return (
                  <div key={sectionTitle || `section-${sidx}`} className={sectionClassName}>
                    <h3 className="mb-4 text-sm leading-[24px] font-medium">
                      <button
                        type="button"
                        className="w-full text-left md:pointer-events-none md:cursor-default"
                        onClick={() => handleMobileToggle(sectionTitle, isOpen)}
                      >
                        {sectionTitle}
                        <span className="float-right inline-block md:hidden">
                          <i
                            className={`icomoon transition-transform before:content-['\\e909'] ${
                              isOpen ? "rotate-180" : ""
                            }`}
                          ></i>
                        </span>
                      </button>
                    </h3>

                    <ul className={isMobile && !isOpen ? "hidden" : ""}>
                      {(section.links || []).map((link, linkIdx: number) => (
                        <li className="my-1 leading-[18.2px]" key={link.linkTitle || linkIdx}>
                          <BFLLink
                            href={link.linkUrl || "#"}
                            className="text-[10px] leading-[19.2px] text-white opacity-80 transition-opacity hover:opacity-100"
                          >
                            {link.linkTitle}
                          </BFLLink>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          );
        })}

        {/* Render Special Sections (6th Column) */}
        <div className="md:col-start-6">
          {specialSections.map((section, index) => {
            if (!section) return null;
            return (
              <div
                key={section.sectionTitle || `special-section-${index}`}
                className={index > 0 ? "mt-3" : ""}
              >
                <h3 className="mb-1 text-sm leading-[24px]">
                  <BFLLink
                    href={section.titleUrl || "#"}
                    className="text-sm font-medium text-white opacity-90 transition-opacity hover:opacity-100"
                  >
                    {section.sectionTitle}
                  </BFLLink>
                </h3>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Products;
